import { GenericDataRecord } from '@cloudio-saas/datasource-types';
import { emptyFunction, useStateXValue } from '@cloudio-saas/statex';
import { useCurrentDataStore } from 'components/DataStoreReference/DataStoreReference';
import { useEffect } from 'react';
import { MISSING_STORE_PATH } from 'store/Store';

export const useTreeState = (
  nodeIdAttribute: string,
  parentNodeIdAttribute: string,
  initialExpanded: boolean,
): {
  expandAll: () => void;
  collapseAll: () => void;
} => {
  const store = useCurrentDataStore<GenericDataRecord>();
  // const records = useStoreRecords(store);

  useEffect(() => {
    if (store && nodeIdAttribute && parentNodeIdAttribute) {
      store.initializeTreeState(
        nodeIdAttribute,
        parentNodeIdAttribute,
        initialExpanded,
        false,
      );
    }
  }, [initialExpanded, nodeIdAttribute, parentNodeIdAttribute, store]);

  if (!(nodeIdAttribute && parentNodeIdAttribute)) {
    return { expandAll: emptyFunction, collapseAll: emptyFunction };
  }

  const expandAll = () => {
    store?.expandAll();
  };

  const collapseAll = () => {
    store?.collapseAll();
  };

  return {
    expandAll,
    collapseAll,
  };
};

export const useTreeStateForNode = (
  _id: string,
  nodeId: string,
): {
  expanded: boolean;
  setExpanded: (exp: boolean) => void;
  depth: number;
  isLeaf: boolean;
} => {
  const store = useCurrentDataStore();
  const expanded = useStateXValue<boolean>(
    [...(store?.treeStatePath ?? MISSING_STORE_PATH), 'state', _id, 'expanded'],
    false,
  );
  const children = useStateXValue<string[]>(
    [...(store?.treeStatePath ?? MISSING_STORE_PATH), 'state', _id, 'children'],
    [],
  );
  const depth = _id && store ? store.getNodeDepth(_id) : 0;
  const setExpanded = (currExpandState: boolean) => {
    if (currExpandState) {
      store?.expandTreeNode(_id, nodeId);
    } else {
      store?.collapseTree(_id);
    }
  };

  return { expanded, setExpanded, depth, isLeaf: children.length === 0 };
};

export const useTreeStateForStore = () => {
  const store = useCurrentDataStore();
  const expandAll = () => {
    store?.expandAll();
  };

  const collapseAll = () => {
    store?.collapseAll();
  };

  return {
    expandAll,
    collapseAll,
  };
};
